import { Link } from 'react-router-dom'
import { ApiError } from '../../lib/api'

/**
 * What a reader sees when the record itself is not there.
 *
 * The two denials have their own screens: a queued seat is `SeatNotice` and a
 * licence refusal is `AccessNotice`, and neither is a generic error. This is
 * the third case, which those two return null for: the identifier does not
 * name anything, or the request failed on the way. An error names the stage
 * that failed and gives the one action that moves the work forward, and a
 * blank page is not an error state.
 */
export function RecordMissing({
  error,
  what,
  onRetry,
}: {
  error: unknown
  what: string
  onRetry?: () => void
}) {
  const status = error instanceof ApiError ? error.status : 0
  // The seat queue, the licence denial and a withdrawal own those statuses.
  if (status === 429 || status === 401 || status === 403 || status === 451) return null

  const missing = status === 404
  const detail = error instanceof Error ? error.message : String(error ?? 'no detail')

  return (
    <div className="mx-auto w-full max-w-[640px] px-4 py-10">
      <div className="chrome mb-2">{missing ? 'Not in this archive' : 'The request failed'}</div>
      <h1 className="text-[32px] leading-[1.04]">
        {missing ? `This ${what} could not be found` : `This ${what} could not be opened`}
      </h1>
      <p className="mt-3 max-w-[62ch] text-[15.5px] leading-normal text-ink2">
        {missing
          ? `The identifier in the address does not name a ${what} this institution holds. Editions are re-ingested from time to time, and an identifier copied from a working note can outlive the record it pointed at. A permalink built from the publication, the date and the page label does not have that problem.`
          : `The archive answered, but not with a ${what}. Nothing has been lost: the record is on the server and the page can be asked for again.`}
      </p>
      <p className="ident mt-3">{detail}</p>
      <div className="mt-5 flex flex-wrap gap-2">
        {onRetry && (
          <button type="button" className="btn-primary" onClick={onRetry}>
            Try again
          </button>
        )}
        <Link to="/search" className="btn-secondary no-underline">
          Search the archive
        </Link>
        <Link to="/browse" className="btn-secondary no-underline">
          Browse by date
        </Link>
      </div>
    </div>
  )
}
